import { DiscardDialog } from "@jugl-web/ui-kit/overlay/DiscardDialog";
import { FC, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { NavigationGuardContext } from "./NavigationGuardContext";
import { NavigationGuardContextValue } from "./types";

interface NavigationGuardProviderProps {
  children: ReactNode;
}

/**
 * Provider that prevents navigation when there are unsaved changes (dirty state).
 *
 * This component tracks multiple "dirty sources" (e.g., forms with unsaved changes) and
 * intercepts navigation attempts. When navigation is requested while changes are unsaved,
 * it displays a confirmation dialog before allowing the user to proceed.
 */
export const NavigationGuardProvider: FC<NavigationGuardProviderProps> = ({
  children,
}) => {
  const [pendingAction, setPendingAction] = useState<null | (() => void)>(null);

  const dirtySourcesRef = useRef<Map<string, boolean>>(new Map());

  const isDirty = useCallback(
    () => Array.from(dirtySourcesRef.current.values()).some(Boolean),
    []
  );

  const registerDirtySource = useCallback((id: string, dirty: boolean) => {
    dirtySourcesRef.current.set(id, dirty);

    const cleanup = () => {
      dirtySourcesRef.current.delete(id);
    };

    return cleanup;
  }, []);

  const reset = useCallback(() => {
    dirtySourcesRef.current.clear();
    setPendingAction(null);
  }, []);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (!isDirty()) {
        action();
      } else {
        setPendingAction(() => action);
      }
    },
    [isDirty]
  );

  const confirm = () => {
    pendingAction?.();
    setPendingAction(null);
    reset();
  };

  const cancel = () => {
    setPendingAction(null);
  };

  const contextValue = useMemo<NavigationGuardContextValue>(
    () => ({
      isDirty,
      registerDirtySource,
      requestNavigation,
      reset,
    }),
    [isDirty, registerDirtySource, requestNavigation, reset]
  );

  return (
    <NavigationGuardContext.Provider value={contextValue}>
      {children}
      <DiscardDialog
        isOpen={!!pendingAction}
        onDiscard={confirm}
        onCancel={cancel}
      />
    </NavigationGuardContext.Provider>
  );
};
